import { apiNames, get, patch, post, put, httpDelete } from '@general/api/shared';
import { getCartCalculatedValues, mapApiCartToProps } from '@general/transforms/cart.transform';
import reactQueryClient from '@general/reactQueryClient';

export const getCartById = async ({ cartId }) => {
    try {
        const response = await get(apiNames.TRANSACTION, `carts/${cartId}`);
        return response?.data?.data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const getCartByMemberId = async ({ memberId }) => {
    try {
        const response = await get(apiNames.TRANSACTION, `carts/member/${memberId}`);
        return response?.data?.data;
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const createCart = async () => {
    // {
    //     "id": null,
    //     "cart_id": null,
    //     "member_id": 95243,
    //     "product_id": "3daaecac-4351-ed11-bba1-000d3a9bcaf2",
    //     "product_type": "fee",
    //     "name": null,
    //     "description": null,
    //     "price": 550,
    //     "quantity": 1,
    //     "meta_info": {
    //         "event_code": "2023RRS",
    //         "event_id": "67239f6e-bb3e-4b6f-b977-de10e6824a00",
    //         "event_function_code": "FP",
    //         "event_function_title": "Full Program",
    //         "event_locale": "US",
    //         "event_name": "ICSC@RED RIVER",
    //         "event_registrant_class": "M",
    //         "event_fee_auto_enroll": true,
    //         "product_id": "3daaecac-4351-ed11-bba1-000d3a9bcaf2",
    //         "member_id": 95243,
    //         "member_name": "Gary Rappaport",
    //         "company_name": ""
    //     }
    // }
};

export const updateCart = async ({ cartId, cartData, viewerId }) => {
    const response = await patch({
        apiName: apiNames.TRANSACTION,
        endpoint: `carts/${cartId}`,
        data: cartData,
    });

    return response?.data?.data;
};

export const createCartItem = async ({ cartId, cartItemData, viewerId }) => {
    try {
        const response = await post({
            apiName: apiNames.TRANSACTION,
            endpoint: `carts/${cartId}/items`,
            data: cartItemData,
        });

        const updatedCart = response?.data?.data;
        reactQueryClient.setQueryData(`cart-${viewerId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            const newCartData = { ...existingCartData };
            const existingLineItemIds = newCartData.items.map(item => item.id);
            const newLineItem = updatedCart.items.find(item => !existingLineItemIds.includes(item.id));

            if (newLineItem) {
                newCartData.items.push(newLineItem);
            }

            if (!newCartData.payment_intent && updatedCart.payment_intent) {
                newCartData.payment_intent = { ...updatedCart.payment_intent };
            }

            newCartData.total = updatedCart.total;

            return mapApiCartToProps(newCartData);
        });

        return updatedCart;
    } catch (e) {
        console.log(e);
        return null;
    }
};

export const updateCartItem = async ({ cartId, cartItemId, cartItemData, viewerId }) => {
    try {
        const response = await patch({
            apiName: apiNames.TRANSACTION,
            endpoint: `carts/${cartId}/items/${cartItemId}`,
            data: cartItemData,
        });

        const updatedCart = response?.data?.data;
        reactQueryClient.setQueryData(`cart-${viewerId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            const itemIndex = existingCartData.items.findIndex(item => item.id === cartItemId);
            const newCartData = { ...existingCartData };

            if (itemIndex >= 0) {
                const fieldsUpdated = Object.keys(cartItemData);

                fieldsUpdated.forEach(field => {
                    newCartData.items[itemIndex][field] = updatedCart.items[itemIndex][field];
                });

                newCartData.items[itemIndex].discounts = updatedCart.items[itemIndex].discounts;
                newCartData.items[itemIndex].tax = updatedCart.items[itemIndex].tax;
            }

            newCartData.total = updatedCart.total;
            return {
                ...newCartData,
                _calculated: getCartCalculatedValues(newCartData),
            };
        });

        return updatedCart;
    } catch (e) {
        console.log(e);
        return null;
    }
};

export const replaceCartItem = async ({ cartId, cartItemId, cartItemData, viewerId }) => {
    try {
        const response = await put({
            apiName: apiNames.TRANSACTION,
            endpoint: `carts/${cartId}/items/${cartItemId}`,
            data: cartItemData,
        });

        const updatedCart = response?.data?.data;
        reactQueryClient.setQueryData(`cart-${viewerId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            const itemIndex = existingCartData.items.findIndex(item => item.id === cartItemId);
            const newCartData = { ...existingCartData };

            if (itemIndex >= 0) {
                const fieldsUpdated = Object.keys(cartItemData);

                fieldsUpdated.forEach(field => {
                    newCartData.items[itemIndex][field] = updatedCart.items[itemIndex][field];
                });

                newCartData.items[itemIndex].discounts = updatedCart.items[itemIndex].discounts;
            }

            newCartData.total = updatedCart.total;
            return {
                ...newCartData,
                _calculated: getCartCalculatedValues(newCartData),
            };
        });

        return updatedCart;
    } catch (e) {
        console.log(e);
        return null;
    }
};

export const updateCartEventCredit = async ({ cartId, viewerId, shouldApplyCredit }) => {
    try {
        const response = await patch({
            apiName: apiNames.TRANSACTION,
            endpoint: `carts/${cartId}`,
            data: { apply_event_credit: shouldApplyCredit },
        });

        const updatedCart = response?.data?.data;
        reactQueryClient.setQueryData(`cart-${viewerId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            return {
                ...existingCartData,
                available_company_credit: updatedCart?.available_company_credit,
                available_event_credit: updatedCart?.available_event_credit,
                available_member_credit: updatedCart?.available_member_credit,
                company_credit: updatedCart?.company_credit,
                event_credit: updatedCart?.event_credit,
                member_credit: updatedCart?.member_credit,
                total: updatedCart?.total,
            };
        });

        return updatedCart;
    } catch (e) {
        console.log(e);
        return null;
    }
};

export const deleteCartItem = async ({ cartId, cartItemId, viewerId }) => {
    try {
        const response = await httpDelete({
            apiName: apiNames.TRANSACTION,
            endpoint: `carts/${cartId}/items/${cartItemId}`,
        });

        const updatedCart = response?.data?.data;
        reactQueryClient.setQueryData(`cart-${viewerId}`, existingCartData => {
            if (!existingCartData) {
                return null;
            }

            const itemIndex = existingCartData.items.findIndex(item => item.id === cartItemId);
            if (itemIndex >= 0) {
                existingCartData.items.splice(itemIndex, 1);
            }

            existingCartData.total = updatedCart.total;
            return mapApiCartToProps(existingCartData);
        });

        return updatedCart;
    } catch (e) {
        console.log(e);
        return null;
    }
};

export const checkOutCart = async ({ cartId, billingFirstName, billingLastName, billingZip, viewerId }) => {
    const data = {};

    if (billingFirstName || billingLastName || billingZip) {
        data.first_name = billingFirstName;
        data.last_name = billingLastName;
        data.billingZip = billingZip;
    }

    try {
        const response = await post({
            apiName: apiNames.TRANSACTION,
            endpoint: `carts/${cartId}/checkout`,
            data,
        });

        const orderData = response?.data?.data;

        await reactQueryClient.invalidateQueries({ queryKey: [`cart-${viewerId}`] });

        return orderData;
    } catch (e) {
        console.log(e);
        return null;
    }
};
